import { useCallback, useEffect, useState } from 'react';
import { IDataServices, IOrgConfig, IUserContext, UserContext } from '../types';
import { UserPermissionService } from '../services';
import { IUserPermissionService } from '../services/UserPermissionService';
import { IErrorHandling } from './withErrorHandling';
import { AppGlobals } from '../AppGlobals';

export interface IUseUserContextProps {
    userContext: IUserContext;
    userPermissions: IUserPermissionService;
    orbConfig: IOrgConfig;
}

export const useUserContext = (apiConnected: boolean, { userService, orgConfigService }: IDataServices, { onError }: IErrorHandling): IUseUserContextProps => {
    const [state, setState] = useState<{ UserContext: IUserContext, UserPermissions: UserPermissionService }>({
        UserContext: new UserContext(),
        UserPermissions: new UserPermissionService(new UserContext())
    });

    const orgConfigDefault: IOrgConfig = {
        CustomerID: AppGlobals.CustomerID,
        ID: 0,
        Title: '',
        OrgTypeLabel: '',
        OrgTypeLabelP: '',
        OrgTypeMgrLabel: '',
        OrgTypeMgrLabelP: '',
        OrgL1Label: 'Group',
        OrgL1LabelP: 'Groups',
        OrgMgrL1Lable: 'Director General (DG)',
        OrgMgrL1LabelP: 'Director Generals (DGs)',
        OrgL2Label: 'Directorate',
        OrgL2LabelP: 'Directorates',
        OrgMgrL2Lable: 'Director',
        OrgMgrL2LabelP: 'Directors',
        OrgL3Label: '',
        OrgL3LabelP: '',
        OrgMgrL3Lable: '',
        OrgMgrL3LabelP: ''
    }

    const [orgConfigState, setOrgConfig] = useState<IOrgConfig>(orgConfigDefault);
    const logError = useCallback(onError, [onError]);

    useEffect(() => {
        const loadUserPermissions = async (): Promise<void> => {
            try {
                console.log('in useUserContext - loadUserPermissions');
                const user = await userService?.readMyPermissions();
                console.log('after readMyPermissions', user);
                const uc: IUserContext = {
                    UserId: user?.ID ?? null,
                    Username: user?.Username ?? '',
                    UserEntities: {
                        EntitiesTimestamp: new Date().getTime(),
                        UserRoles: user?.UserRoles ?? [],
                        UserGroups: user?.UserGroups ?? [],
                        UserDirectorates: user?.UserDirectorates ?? [],
                        UserProjects: user?.UserProjects ?? [],
                        UserPartnerOrganisations: user?.UserPartnerOrganisations ?? [],
                        FinancialRiskUserGroups: user?.FinancialRiskUserGroups ?? []
                    },
                    DirectorOf: user?.DirectorateDirectorUsers ?? [],
                    ApproverOfDirectorates: user?.DirectorateReportApproverUsers ?? [],
                    SROOf: user?.ProjectSeniorResponsibleOwnerUsers ?? [],
                    ApproverOfProjects: user?.ProjectReportApproverUsers ?? [],
                    RiskOwnerOf: [...user?.CorporateRiskRiskOwnerUsers ?? [], ...user?.FinancialRiskRiskOwnerUsers ?? []],
                    AlternativeApproverOfRisks: user?.CorporateRiskReportApproverUsers,
                    AlternativeApproverOfFinancialRisks: user?.FinancialRiskReportApproverUsers,
                    LeadPolicySponsorOfPartnerOrgs: user?.PartnerOrganisationLeadPolicySponsorUsers ?? [],
                    ReportAuthorOfPartnerOrgs: user?.PartnerOrganisationReportAuthorUsers ?? [],
                    ContributorTo: user?.ContributorContributorUsers ?? []
                };
                setState({ UserContext: uc, UserPermissions: new UserPermissionService(uc) });
            } catch (err) {
                const error = err as Error;
                logError(`Error loading user context`, error.message);
            }
        };

        if (apiConnected) {
            loadUserPermissions();
        }
    }, [apiConnected, userService, logError]);

    return { userContext: state.UserContext, userPermissions: state.UserPermissions, orbConfig: orgConfigState };
};
